import {
  LABEL_TO_STATUS,
  LABEL_TO_PRIO,
  LABEL_TO_TYPE,
  STATUS_LABEL,
  STATUS_CODE,
  STATUS_BY_CODE,
  PRIO_LABEL,
  PRIO_CODE,
  PRIO_BY_CODE,
  ISSUE_TYPE_MAP,
  TYPE_OPTIONS,
  TYPE_TO_ISSUE_KEY,
} from "../design/domain";

/**
 * 「Excel 列 → 匯入列」的解析與驗證。
 *
 * 為什麼是獨立的純函式模組（不碰 React、不碰 xlsx、不碰 axios）：
 * 1. 這裡是整個匯入功能唯一會出錯的地方 —— 使用者的檔案什麼都可能填。
 *    抽成純函式才測得動（日期序號、各種文字日期、中文選項、空白列、欄位順序…），
 *    不必為了驗一個日期格式去跑一次瀏覽器。
 * 2. 錯誤訊息的用字要跟後端 TaskImportRules 對得起來。前端擋掉的與後端擋掉的
 *    講同一句話，使用者才不會以為是兩種不同的問題。
 *
 * 分工：這裡只做「看得懂／看不懂」與「格式對不對」，
 * 「里程碑存不存在」「指派人是誰」這種要查資料的，前端只在拿得到名單時順手提醒，
 * 真正算數的是後端（同一份檔案別人匯、里程碑剛被刪掉…前端的名單不保證是最新的）。
 *
 * 所以一列的問題分兩種，顯示與後果都不一樣：
 *   errors   前端自己就能斷定不行的（標題空白、日期看不懂、工時是負的）。會擋住匯入。
 *   warnings 要查資料才算數、前端只是「手上這份名單裡沒有」的猜測。只提醒，不擋。
 * 這個分法是必要的：可指派的人在後端是「專案成員或系統使用者」，前端手上卻只有專案成員
 * （Detail.js 傳的是 membersArr），把猜測當錯誤擋的話，一個填了合法系統使用者的檔案會變成
 * 「試跑說全部通過、畫面卻整列紅字、開始匯入按不下去」，而使用者除了回去改 Excel 沒有出路。
 * 後端回報之後更以後端為準（見 rowIssues）：它說這一列可以，前端的猜測就該收回去。
 */

/**
 * 單次匯入的列數上限，與後端 TaskImportRules.MaxRows 必須一致。
 *
 * 原本是 500。每一列在後端都是十幾次資料庫往返（發號、階層彙總、自動關注、活動紀錄、
 * 通知落地），整批又包在同一個交易裡，500 列跑不完就會撞上前端逾時 ——
 * 而 axios 放棄不會停掉伺服器，使用者看到「失敗」、資料卻可能已經全部寫進去，
 * 照著訊息重匯一次就是整批重複。降到 200，再加上匯入專用的逾時
 * （TaskImportDialog 的 IMPORT_TIMEOUT_MS）才兜得起來。
 */
export const MAX_IMPORT_ROWS = 200;
/** 與 project_task.title、後端 ReqPostTaskValidator 相同 */
export const TITLE_MAX = 300;
/** 與 project_task.description 相同 */
export const DESCRIPTION_MAX = 4000;
/** 與 project_task.tags 相同 */
export const TAGS_MAX = 500;

/** 類型／狀態／優先級的可選中文，錯誤訊息與範本的「欄位說明」共用同一份 */
/*
 * 類型的可選清單走 TYPE_OPTIONS（八個白話名，順序同設計稿），不要從 LABEL_TO_TYPE
 * 的鍵推導：那份對照表為了讀得懂舊資料，同時收了「任務／缺陷／史詩／使用者故事／
 * 子任務」五個舊名，推導出來會變成十三項，同一個型別列兩次，看起來像十種不同的選擇。
 * 舊名只是別名，照下面 PRIO_CHOICES 的「中」與 TYPE_LABEL_ALIAS 的「故事」一樣不列進來。
 */
export const TYPE_CHOICES = TYPE_OPTIONS;
export const STATUS_CHOICES = Object.keys(LABEL_TO_STATUS);
/** 優先級的「中」是「一般」的別名，不列進可選清單免得看起來有五個等級 */
export const PRIO_CHOICES = Object.keys(LABEL_TO_PRIO).filter((x) => x !== "中");

/**
 * 欄位定義。
 *
 * aliases 是為了讓使用者自己整理的檔案也能直接匯 —— 大家習慣寫「工項名稱」「負責人」
 * 「到期日」，硬要他們改成跟系統一模一樣的字，等於還是要手動整理一遍。
 * 只要標題列認得出來，欄位順序不同也沒關係（比對的是標題，不是第幾欄）。
 */
export const IMPORT_COLUMNS = [
  {
    key: "title",
    /*
     * label 是產生範本時標題列的用字，一律跟「新增工項」表單一致（forms.js 的 items）。
     * 同一個欄位在表單叫「工項名稱」、在範本叫「標題」，使用者對照著填一定會困惑。
     * 舊的寫法留在 aliases 裡，先前已經匯出的檔案照樣匯得進來；
     * 而且 normalizeHeader 會抹平空白與括號，改 label 不影響比對。
     */
    label: "工項名稱",
    required: true,
    aliases: ["標題", "工項名稱", "名稱", "主旨", "title", "name", "summary"],
    note: `必填。最多 ${TITLE_MAX} 字。`,
  },
  {
    key: "issueType",
    label: "類型",
    aliases: ["類型", "型別", "議題類型", "工項類型", "type", "issuetype"],
    note: `可留白（預設「工作」）。可填：${TYPE_CHOICES.join("、")}。`,
  },
  {
    key: "phase",
    label: "里程碑",
    /* 主要欄名是「里程碑」。舊名稱全部留著當別名：這個欄位歷來叫過「階段」「交付目標」，
       使用者手上的舊範本不能因為改個顯示名稱就匯不進來 */
    aliases: ["里程碑", "所屬里程碑", "交付目標", "所屬交付目標", "階段", "phase", "milestone"],
    note: "可留白（＝尚未排入任何里程碑）。填寫時須為本專案既有的里程碑名稱。",
  },
  {
    key: "assignee",
    label: "指派人",
    aliases: ["指派人", "負責人", "執行人", "assignee", "owner"],
    note: "可留白（＝未指派）。填姓名或帳號；姓名重複時請改填帳號。",
  },
  {
    key: "status",
    label: "狀態",
    aliases: ["狀態", "status"],
    note: `可留白（預設「待處理」）。可填：${STATUS_CHOICES.join("、")}。`,
  },
  {
    key: "priority",
    label: "優先級",
    aliases: ["優先級", "優先序", "優先順序", "priority"],
    note: `可留白（預設「一般」）。可填：${PRIO_CHOICES.join("、")}。`,
  },
  {
    key: "startDate",
    label: "開始日",
    aliases: ["開始日", "開始日期", "起始日", "startdate", "start"],
    note: "可留白。格式為 2026/9/18 或 2026-09-18；儲存格亦可使用日期格式。",
  },
  {
    key: "dueDate",
    label: "截止日",
    aliases: ["截止日", "截止日期", "到期日", "完成期限", "duedate", "due"],
    note: "可留白。格式同開始日，且不可早於開始日。",
  },
  {
    key: "estimatedHours",
    label: "預估工時 (h)",
    aliases: ["預估工時", "預估工時(h)", "工時", "estimatedhours", "estimate"],
    note: "可留白。數字，單位小時，不可為負數。",
  },
  {
    key: "tags",
    label: "標籤",
    aliases: ["標籤", "tag", "tags", "labels"],
    note: `可留白。以逗號分隔，最多 ${TAGS_MAX} 字。（新增工項表單不提供此欄位，僅能透過匯入填寫）`,
  },
  {
    key: "description",
    label: "說明",
    aliases: ["說明", "描述", "需求說明", "內容", "description", "detail"],
    note: `可留白。最多 ${DESCRIPTION_MAX} 字。`,
  },
  {
    key: "parentIssueKey",
    label: "父工項代碼",
    aliases: ["父工項代碼", "父工項", "上層工項", "parent", "parentissuekey"],
    note: "可留白。填寫本專案既有工項的代碼（例如 EL-12），此列將歸於該工項之下。（新增工項表單不提供此欄位，僅能透過匯入填寫）",
  },
];

/** key → 欄位定義 */
const COLUMN_BY_KEY = IMPORT_COLUMNS.reduce((acc, c) => {
  acc[c.key] = c;
  return acc;
}, {});

/** 送後端時的欄位名（對應 ReqImportTaskRow） */
const PAYLOAD_FIELD = {
  title: "Title",
  issueType: "IssueType",
  phase: "Phase",
  assignee: "Assignee",
  status: "Status",
  priority: "Priority",
  startDate: "StartDate",
  dueDate: "DueDate",
  estimatedHours: "EstimatedHours",
  tags: "Tags",
  description: "Description",
  parentIssueKey: "ParentIssueKey",
};

/* ===================== 基本轉換 ===================== */

/**
 * 儲存格 → 去頭尾空白的字串。
 * xlsx 可能給 Date（儲存格是日期格式又開了 cellDates）、數字或布林，一律先轉成字串再處理。
 */
export function cellText(v) {
  if (v === null || v === undefined) return "";
  if (v instanceof Date) {
    if (Number.isNaN(v.getTime())) return "";
    const p = (n) => String(n).padStart(2, "0");
    return `${v.getFullYear()}-${p(v.getMonth() + 1)}-${p(v.getDate())}`;
  }
  return String(v).trim();
}

/**
 * 標題文字正規化：拿掉所有空白（含全形）、括號內的單位、必填星號，再轉小寫。
 * 「預估工時 (h)」「預估工時（小時）」「標題 *」都要對得到同一個欄位。
 */
export function normalizeHeader(text) {
  return cellText(text)
    .replace(/[（(][^）)]*[）)]/g, "")
    .replace(/[\s　]/g, "")
    .replace(/[*＊]/g, "")
    .toLowerCase();
}

/** Excel 日期序號 → YYYY-MM-DD（原點 1899/12/30，吸收 Excel 把 1900 當閏年的歷史錯誤） */
export function excelSerialToISO(serial) {
  const n = Number(serial);
  if (!Number.isFinite(n) || n < 1 || n > 2958465) return "";
  // 用 UTC 推算再取 UTC 欄位：用本地時間會因為時區與日光節約在某些日期整整差一天
  const ms = Date.UTC(1899, 11, 30) + Math.round(n * 86400000);
  const d = new Date(ms);
  if (Number.isNaN(d.getTime())) return "";
  const p = (x) => String(x).padStart(2, "0");
  return `${d.getUTCFullYear()}-${p(d.getUTCMonth() + 1)}-${p(d.getUTCDate())}`;
}

/**
 * 日期：接受 Excel 日期序號與常見文字格式，一律轉成後端要的 YYYY-MM-DD。
 * 空白不是錯誤（開始日、截止日本來就可以不填）。
 */
export function parseImportDate(raw, columnLabel = "日期") {
  const v = cellText(raw);
  if (!v) return { value: "", error: "" };

  // 純數字（且不含 - 或 /）視為 Excel 日期序號
  if (/^\d+(\.\d+)?$/.test(v)) {
    const iso = excelSerialToISO(v);
    if (iso) return { value: iso, error: "" };
  }

  // 2026/9/18、2026-09-18、2026.9.18、2026年9月18日（後面可再接時間，時間一律忽略）
  const m = v.match(/^(\d{4})\s*[/\-.年]\s*(\d{1,2})\s*[/\-.月]\s*(\d{1,2})\s*日?(?:[T\s].*)?$/);
  if (m) {
    const iso = toISO(Number(m[1]), Number(m[2]), Number(m[3]));
    if (iso) return { value: iso, error: "" };
  }

  // 20260918
  const compact = v.match(/^(\d{4})(\d{2})(\d{2})$/);
  if (compact) {
    const iso = toISO(Number(compact[1]), Number(compact[2]), Number(compact[3]));
    if (iso) return { value: iso, error: "" };
  }

  return {
    value: "",
    error: `「${columnLabel}」的「${v}」不是看得懂的日期，請填 2026/9/18 或 2026-09-18`,
  };
}

/** 年月日 → YYYY-MM-DD；2026/2/30 這種不存在的日期回空字串（由呼叫端當成格式錯） */
function toISO(y, m, d) {
  if (!y || !m || !d || m < 1 || m > 12 || d < 1 || d > 31) return "";
  const dt = new Date(Date.UTC(y, m - 1, d));
  if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== m - 1 || dt.getUTCDate() !== d) return "";
  const p = (x) => String(x).padStart(2, "0");
  return `${y}-${p(m)}-${p(d)}`;
}

/** 預估工時：容許「8h」「8 小時」，非數字或負數一律當錯誤 */
export function parseImportHours(raw) {
  const original = cellText(raw);
  if (!original) return { value: "", error: "" };

  const v = original
    .replace(/小時/g, "")
    .replace(/hr?s?$/i, "")
    .trim();

  if (!v || !/^-?\d+(\.\d+)?$/.test(v)) {
    return { value: "", error: `「預估工時」的「${original}」不是數字` };
  }
  const n = Number(v);
  if (n < 0) return { value: "", error: `「預估工時」的「${original}」是負數，請填 0 或正數` };
  return { value: String(n), error: "" };
}

/**
 * 類型的中文別名。
 *
 * 後端 TaskImportRules.IssueTypeMap 有 ["故事"] = "story"，前端三份對照表
 * （LABEL_TO_TYPE 只有「使用者故事」、ISSUE_TYPE_MAP、TYPE_TO_ISSUE_KEY）都沒有它 ——
 * 同一個字在前端是錯、在後端是對，於是一份後端收得下的檔案在畫面上整列紅字，
 * 而「開始匯入」因為 sum.bad > 0 被停用，使用者除了回去改 Excel 沒有出路。
 * 兩邊收的範圍必須一樣（同 STATUS_KEY_ALIAS 的理由）。
 *
 * 不把「故事」加進 TYPE_CHOICES：那是可選值清單（錯誤訊息與範本的「欄位說明」共用），
 * 別名列進去會看起來像兩個不同的型別，同優先級的「中」也是這樣處理的。
 */
const TYPE_LABEL_ALIAS = { 故事: "使用者故事" };

/** 類型：中文、設計稿鍵或後端 issue_type.key 皆可 → { key(後端), label(中文) } */
export function parseImportType(raw) {
  const v = cellText(raw);
  // 留白的預覽標籤要跟全站一致：同一個型別在別處叫「工作」，這裡不能還印舊名「任務」
  if (!v) return { key: "task", label: "工作", error: "" };

  // 英文鍵不分大小寫：後端的對照表是 OrdinalIgnoreCase，前端比它嚴只會擋掉後端收得下的列
  const lower = v.toLowerCase();
  const designKey =
    LABEL_TO_TYPE[v] ||
    LABEL_TO_TYPE[TYPE_LABEL_ALIAS[v]] ||
    ISSUE_TYPE_MAP[v] ||
    ISSUE_TYPE_MAP[lower] ||
    (TYPE_TO_ISSUE_KEY[v] ? v : "") ||
    (TYPE_TO_ISSUE_KEY[lower] ? lower : "");
  if (!designKey) {
    return { key: "", label: "", error: `「類型」的「${v}」不是可選的值，可填：${TYPE_CHOICES.join("、")}` };
  }
  const label = Object.keys(LABEL_TO_TYPE).find((k) => LABEL_TO_TYPE[k] === designKey) || v;
  return { key: TYPE_TO_ISSUE_KEY[designKey] || "task", label, error: "" };
}

/**
 * 狀態鍵的別名。後端 TaskImportRules.StatusMap 收得下 todo，前端原本不認得，
 * 於是同一個字在前端是錯、在後端是對 —— 兩邊擋的東西不一樣，使用者就會看到
 * 自相矛盾的畫面。兩邊收的範圍必須一樣。
 */
const STATUS_KEY_ALIAS = { todo: "pending" };

/** 狀態：中文、鍵值或數字皆可 → 中文（送後端與顯示都用中文，後端兩種都收） */
export function parseImportStatus(raw) {
  const v = cellText(raw);
  if (!v) return { value: "待處理", error: "" };

  const lower = v.toLowerCase();
  const key =
    LABEL_TO_STATUS[v] ||
    STATUS_KEY_ALIAS[lower] ||
    (STATUS_CODE[lower] !== undefined ? lower : "") ||
    STATUS_BY_CODE[Number(v)];
  if (!key || !STATUS_LABEL[key]) {
    return { value: "", error: `「狀態」的「${v}」不是可選的值，可填：${STATUS_CHOICES.join("、")}` };
  }
  return { value: STATUS_LABEL[key], error: "" };
}

/** 優先級：中文、鍵值或數字皆可 → 中文 */
export function parseImportPriority(raw) {
  const v = cellText(raw);
  if (!v) return { value: "一般", error: "" };

  const lower = v.toLowerCase();
  const key = LABEL_TO_PRIO[v] || (PRIO_CODE[lower] !== undefined ? lower : "") || PRIO_BY_CODE[Number(v)];
  if (!key || !PRIO_LABEL[key]) {
    return { value: "", error: `「優先級」的「${v}」不是可選的值，可填：${PRIO_CHOICES.join("、")}` };
  }
  return { value: PRIO_LABEL[key], error: "" };
}

/* ===================== 標題列與整張表 ===================== */

/**
 * 標題列 → { 欄位 key: 第幾欄 }。
 * 比對的是標題文字，所以欄位順序、多餘欄位都無所謂；認不得的標題收進 unknown 提醒使用者。
 */
export function mapHeaderRow(cells) {
  const map = {};
  const unknown = [];
  (cells || []).forEach((cell, idx) => {
    const text = cellText(cell);
    if (!text) return;
    const norm = normalizeHeader(cell);
    const hit = IMPORT_COLUMNS.find((c) => c.aliases.some((a) => normalizeHeader(a) === norm));
    if (!hit) {
      unknown.push(text);
      return;
    }
    // 同一個欄位出現兩次時以第一次為準：後面那欄多半是使用者自己加的備註欄
    if (map[hit.key] === undefined) map[hit.key] = idx;
  });

  const missingRequired = IMPORT_COLUMNS.filter((c) => c.required && map[c.key] === undefined).map((c) => c.label);
  return { map, unknown, missingRequired };
}

/** 整列都是空的（使用者常在資料中間留空行，或 Excel 把幾千列空列一起帶出來） */
export function isBlankRow(cells) {
  return !(cells || []).some((c) => cellText(c) !== "");
}

/**
 * 整張工作表（sheet_to_json 的 array-of-arrays）→ 可預覽、可送出的匯入列。
 *
 * ctx（皆可省略，省略就不做該項檢查）：
 *   phaseNames  本專案的里程碑名稱
 *   people      [{ name, account }] 可指派的人
 *   issueKeys   本專案既有工項代碼（驗父工項）
 *
 * originRowNo：aoa 的第 0 列在 Excel 上是第幾列。
 *   sheet_to_json 是從工作表 !ref 的起點開始輸出，不是從第 1 列 —— 檔案的 dimension 若是
 *   A3:L20（使用者在上面留了空列，或檔案是別的系統產的），索引 0 其實是 Excel 的第 3 列。
 *   少了這個位移，所有錯誤訊息講的列號都會指錯，使用者照著去看會看到另一列。
 *   呼叫端用 XLSX.utils.decode_range(sheet["!ref"]).s.r + 1 算出來傳進來。
 *
 * 回傳 { error } 代表整張表不能用（找不到標題列、缺必填欄、超過列數上限）；
 * 否則回 { rows, headerRowNo, unknownHeaders }。
 */
export function parseSheet(aoa, ctx = {}, originRowNo = 1) {
  const table = Array.isArray(aoa) ? aoa : [];
  const origin = Number.isFinite(Number(originRowNo)) && Number(originRowNo) > 0 ? Number(originRowNo) : 1;
  /** aoa 索引 → Excel 上的列號 */
  const rowNoOf = (i) => origin + i;

  const headerIdx = table.findIndex((r) => !isBlankRow(r));
  if (headerIdx < 0) return { error: "此檔案為空白，沒有任何內容" };

  const { map, unknown, missingRequired } = mapHeaderRow(table[headerIdx]);
  if (missingRequired.length) {
    return {
      error: `第 ${rowNoOf(headerIdx)} 列看起來是欄位標題，但找不到「${missingRequired.join(
        "」「"
      )}」欄。請先下載範本，或把第一列改成欄位標題（例如：${IMPORT_COLUMNS.map((c) => c.label).join("、")}）`,
    };
  }

  const dataRows = [];
  for (let i = headerIdx + 1; i < table.length; i += 1) {
    const cells = table[i] || [];
    // 空白列略過而不是報錯：使用者常用空行分段，那不是資料錯誤
    if (isBlankRow(cells)) continue;
    dataRows.push({ rowNo: rowNoOf(i), cells });
  }

  if (dataRows.length === 0) {
    return { error: "標題列底下沒有任何資料列，請確認第二列開始是要匯入的工項" };
  }
  if (dataRows.length > MAX_IMPORT_ROWS) {
    return {
      error: `單次匯入上限 ${MAX_IMPORT_ROWS} 列，此檔案共 ${dataRows.length} 列，請拆分為多個檔案分批匯入`,
    };
  }

  const rows = dataRows.map(({ rowNo, cells }) => buildRow(rowNo, cells, map, ctx));
  return { rows, headerRowNo: rowNoOf(headerIdx), unknownHeaders: unknown };
}

/** 一列：原始值 → 正規化後的顯示值 + 送後端的 payload + 這一列的錯誤與提醒 */
function buildRow(rowNo, cells, map, ctx) {
  const rawOf = (key) => (map[key] === undefined ? "" : cellText(cells[map[key]]));
  const errors = [];
  const warnings = [];
  const values = {};

  const title = rawOf("title");
  if (!title) errors.push("「標題」不可空白");
  else if (title.length > TITLE_MAX) errors.push(`「標題」有 ${title.length} 字，超過 ${TITLE_MAX} 字上限`);
  values.title = title;

  const type = parseImportType(rawOf("issueType"));
  if (type.error) errors.push(type.error);
  values.issueType = type.label || rawOf("issueType");

  const status = parseImportStatus(rawOf("status"));
  if (status.error) errors.push(status.error);
  values.status = status.value || rawOf("status");

  const prio = parseImportPriority(rawOf("priority"));
  if (prio.error) errors.push(prio.error);
  values.priority = prio.value || rawOf("priority");

  const start = parseImportDate(rawOf("startDate"), "開始日");
  if (start.error) errors.push(start.error);
  values.startDate = start.value || rawOf("startDate");

  const due = parseImportDate(rawOf("dueDate"), "截止日");
  if (due.error) errors.push(due.error);
  values.dueDate = due.value || rawOf("dueDate");

  // 兩個日期都看得懂才比大小；其中一個格式錯的話再比一次只是多一則看不懂的錯誤
  if (!start.error && !due.error && start.value && due.value && due.value < start.value) {
    errors.push(`「截止日」${due.value} 早於「開始日」${start.value}`);
  }

  const hours = parseImportHours(rawOf("estimatedHours"));
  if (hours.error) errors.push(hours.error);
  values.estimatedHours = hours.value || rawOf("estimatedHours");

  const description = rawOf("description");
  if (description.length > DESCRIPTION_MAX) {
    errors.push(`「說明」有 ${description.length} 字，超過 ${DESCRIPTION_MAX} 字上限`);
  }
  values.description = description;

  const tags = rawOf("tags");
  if (tags.length > TAGS_MAX) errors.push(`「標籤」有 ${tags.length} 字，超過 ${TAGS_MAX} 字上限`);
  values.tags = tags;

  /*
   * 里程碑、指派人、父工項要查資料才知道對不對，所以一律是「提醒」而不是「錯誤」：
   *
   * 前端手上的名單既不完整也不保證是最新的 —— 可指派的人在後端是「專案成員或系統使用者」，
   * 這裡拿到的只有專案成員；里程碑、父工項也可能是別人剛建好而這一頁還沒重新載入。
   * 把它當錯誤擋下來，使用者會遇到「後端說可以、畫面卻不讓他匯」而且無法可解的死路。
   * 打錯字時先講一聲仍然有價值（省一趟往返），所以還是要提醒，只是不擋。
   *
   * 沒有名單（還沒載入、沒權限）就完全不提，免得每一列都掛著一句沒有根據的話。
   */
  const phase = rawOf("phase");
  if (phase && Array.isArray(ctx.phaseNames) && ctx.phaseNames.length) {
    // 不分大小寫：後端用的是 OrdinalIgnoreCase，前端比它嚴只會提醒一件後端根本不介意的事
    if (!ctx.phaseNames.some((n) => String(n).toLowerCase() === phase.toLowerCase())) {
      warnings.push(`「里程碑」在本專案的名單中找不到「${phase}」。本專案的里程碑：${ctx.phaseNames.slice(0, 10).join("、")}`);
    }
  }
  values.phase = phase;

  const assignee = rawOf("assignee");
  if (assignee && Array.isArray(ctx.people) && ctx.people.length) {
    const lower = assignee.toLowerCase();
    const hits = ctx.people.filter(
      (p) => String(p.name || "").toLowerCase() === lower || String(p.account || "").toLowerCase() === lower
    );
    if (hits.length === 0) {
      // 「專案成員」而不是「系統裡沒有這個人」：後端也收非本專案成員的系統使用者
      warnings.push(`「指派人」不在本專案成員名單中：「${assignee}」。若該人員為其他部門的系統使用者，匯入時將由後端確認`);
    } else if (hits.length > 1 && !hits.some((p) => String(p.account || "").toLowerCase() === lower)) {
      warnings.push(`「指派人」的「${assignee}」對應到 ${hits.length} 個帳號，無法判斷是哪一位，請改填帳號`);
    }
  }
  values.assignee = assignee;

  const parent = rawOf("parentIssueKey");
  if (parent && Array.isArray(ctx.issueKeys) && ctx.issueKeys.length) {
    if (!ctx.issueKeys.some((k) => String(k).toLowerCase() === parent.toLowerCase())) {
      warnings.push(`「父工項代碼」在本專案的工項中找不到「${parent}」（請填寫本專案既有工項的代碼）`);
    }
  }
  values.parentIssueKey = parent;

  /*
   * 送後端的值：看得懂的用正規化後的（日期已轉成 YYYY-MM-DD），
   * 看不懂的原樣送上去 —— 後端會再驗一次，讓兩邊回報同一個問題，
   * 而不是前端偷偷把它清成空白，使用者以為修好了。
   */
  const payload = { RowNo: rowNo };
  payload[PAYLOAD_FIELD.title] = title;
  payload[PAYLOAD_FIELD.issueType] = type.key || rawOf("issueType");
  payload[PAYLOAD_FIELD.phase] = phase;
  payload[PAYLOAD_FIELD.assignee] = assignee;
  payload[PAYLOAD_FIELD.status] = status.value || rawOf("status");
  payload[PAYLOAD_FIELD.priority] = prio.value || rawOf("priority");
  payload[PAYLOAD_FIELD.startDate] = start.value || rawOf("startDate");
  payload[PAYLOAD_FIELD.dueDate] = due.value || rawOf("dueDate");
  payload[PAYLOAD_FIELD.estimatedHours] = hours.value || rawOf("estimatedHours");
  payload[PAYLOAD_FIELD.tags] = tags;
  payload[PAYLOAD_FIELD.description] = description;
  payload[PAYLOAD_FIELD.parentIssueKey] = parent;

  return {
    rowNo,
    values,
    payload,
    /** 前端就能斷定不行的，會擋住匯入 */
    errors,
    /** 要查資料才算數的猜測，只提醒不擋 */
    warnings,
    /** 後端試跑／匯入之後回填 */
    serverOk: null,
    serverMessage: "",
    issueKey: "",
  };
}

/** 把後端逐列結果併回預覽列（對不到的列保持原樣，不要把訊息掛錯列） */
export function mergeServerResults(rows, results) {
  const byRow = {};
  (results || []).forEach((r) => {
    const no = Number(r.row_no ?? r.rowNo);
    if (Number.isFinite(no)) byRow[no] = r;
  });
  return (rows || []).map((row) => {
    const hit = byRow[row.rowNo];
    if (!hit) return row;
    return {
      ...row,
      serverOk: hit.ok === true,
      /*
       * rolled_back：整批復原時，後端把「同一批有別的列寫入失敗」也回成 ok:false。
       * 那是這一批的結果，不是這一列的問題 —— 少了這個旗標，199 列本身完全正確的資料
       * 會一起變成紅字、統計寫「有問題 200 筆」，連「開始匯入」都跟著停用，
       * 而使用者其實只要修出事的那一列就好（見 rowIssues / rowWarnings）。
       */
      serverRolledBack: hit.rolled_back === true,
      serverMessage: String(hit.message || ""),
      issueKey: String(hit.issue_key || hit.issueKey || ""),
    };
  });
}

/**
 * 某一列要顯示的錯誤（會擋住匯入的那種）。
 *
 * 後端驗過之後就以後端為準：它說這一列可以（serverOk === true），前端自己的判斷一律撤掉。
 * 少了這一步，「試跑說全部通過」與「那一列還是紅字、開始匯入按不下去」會同時出現在畫面上，
 * 而使用者沒有任何辦法讓那個紅字消失。後端說不行時才把兩邊的訊息合起來講。
 */
export function rowIssues(row) {
  if (row.serverOk === true) return [];

  /*
   * 被整批復原連累的列：後端回 ok:false 是因為「這一列沒有被建立」，不是它有問題。
   * 這裡不把它算成錯誤，統計才不會寫「有問題 200 筆」，「開始匯入」也才不會在
   * 使用者修好那一列之後仍然按不下去。前端自己驗出來的錯誤照舊保留（實務上是空的 ——
   * 有錯就不會走到寫入 —— 但檔案在送出後被改掉時仍然該顯示）。
   */
  if (row.serverRolledBack) return [...(row.errors || [])];

  const list = [...(row.errors || [])];
  if (row.serverOk === false && row.serverMessage && !list.includes(row.serverMessage)) {
    list.push(row.serverMessage);
  }
  return list;
}

/**
 * 某一列要顯示的提醒（不擋匯入）。
 * 後端已經驗過（不管過或沒過）就不再顯示前端的猜測 —— 那時已經有真正算數的答案了。
 */
export function rowWarnings(row) {
  // 被整批復原連累的列：用琥珀色的提醒講「這一列沒有被建立」，而不是紅字說它有問題
  if (row.serverRolledBack && row.serverMessage) return [row.serverMessage];
  if (row.serverOk !== null && row.serverOk !== undefined) return [];
  return [...(row.warnings || [])];
}

/** 統計：可匯入 / 有問題 / 有提醒 */
export function summarize(rows) {
  const list = rows || [];
  const bad = list.filter((r) => rowIssues(r).length > 0).length;
  const warn = list.filter((r) => rowIssues(r).length === 0 && rowWarnings(r).length > 0).length;
  return { total: list.length, ok: list.length - bad, bad, warn };
}

/* ===================== 範本 ===================== */

/** 範本的欄位標題列 */
export function templateHeaders() {
  return IMPORT_COLUMNS.map((c) => c.label);
}

/** 範本的範例列（一筆填好的資料，讓使用者照著改就好） */
export function templateSample() {
  return IMPORT_COLUMNS.map((c) => {
    switch (c.key) {
      case "title":
        return "會員可以用手機號碼登入";
      case "issueType":
        return "使用者故事";
      case "phase":
        return "";
      case "assignee":
        return "";
      case "status":
        return "待處理";
      case "priority":
        return "一般";
      case "startDate":
        return "2026/9/18";
      case "dueDate":
        return "2026/9/30";
      case "estimatedHours":
        return "16";
      case "tags":
        return "登入,會員";
      case "description":
        return "支援手機號碼 + 驗證碼登入，含重送與鎖定規則。";
      case "parentIssueKey":
        return "";
      default:
        return "";
    }
  });
}

/** 範本的「欄位說明」工作表：哪些必填、選項有哪些、日期格式 */
export function templateNotes() {
  return [
    ["欄位", "必填", "說明"],
    ...IMPORT_COLUMNS.map((c) => [c.label, c.required ? "必填" : "選填", COLUMN_BY_KEY[c.key].note]),
    [],
    ["共通規則", "", `一次最多 ${MAX_IMPORT_ROWS} 列；第一列必須是欄位標題，欄位順序可以自己調整。`],
    ["", "", "中間的空白列會自動略過；標題重複不算錯誤（同名工項是允許的）。"],
    ["", "", "日期可以填 2026/9/18、2026-09-18，或直接用 Excel 的日期格式儲存格。"],
    ["", "", "有任何一列有問題時整批都不會寫入，修正後重新匯入即可，不會匯到一半。"],
  ];
}
